import isEmail from 'validator/lib/isEmail';
import isIBAN from 'validator/lib/isIBAN';
import isBefore from 'validator/lib/isBefore';
import isISO8601 from 'validator/lib/isISO8601';
import isPostalCode, { PostalCodeLocale } from 'validator/lib/isPostalCode';
import { FormField } from '../state-machines/form-machine';

type isInComparedValue = (number | string | undefined)[];

export type ValidatorFn<Args extends any[] = any[]> = (
  ...args: Args
) => (
  value: string | number | undefined,
  fields?: FormField[]
) => undefined | string[];

export const required: ValidatorFn = (errMsg: string) => (v) => {
  return v !== undefined && v !== '' ? undefined : [errMsg];
};

export const isOneOf: ValidatorFn =
  (values: any[], errMsg: string) => (value) => {
    return values.includes(value) ? undefined : [errMsg];
  };

export const isNotOneOf: ValidatorFn =
  (values: any[], errMsg: string) => (value) => {
    return !values.includes(value) ? undefined : [errMsg];
  };

export const exists: ValidatorFn = (errMsg: string) => (value) => {
  return value !== undefined && value !== null ? undefined : [errMsg];
};

export const requiredValue: ValidatorFn =
  (errMsg: string, option: string) => (v) => {
    const convertToBoolean = (value) => {
      if (value === '1' || value === 'default' || value === 'true') {
        return 'true';
      }
      return 'false';
    };

    if (option == 'true' || option == 'false') {
      const valueToBoolean = convertToBoolean(v);
      return valueToBoolean !== option ? [errMsg] : undefined;
    }

    const value = toString(v).toLowerCase();
    return value && value !== option.toLowerCase() ? [errMsg] : undefined;
  };

export const isEqual: ValidatorFn = (comparedValue, errMsg: string) => (v) => {
  return v === comparedValue ? undefined : [errMsg];
};

export const isIn: ValidatorFn =
  (comparedValue: isInComparedValue, errMsg = 'err') =>
  (v) => {
    return comparedValue.includes(v) ? undefined : [errMsg];
  };

export const notIsIn: ValidatorFn =
  (comparedValue: isInComparedValue, errMsg = 'err') =>
  (v) => {
    return !comparedValue.includes(v) ? undefined : [errMsg];
  };

export const isNotEqual: ValidatorFn =
  (comparedValue, errMsg: string) => (v) => {
    return v !== comparedValue ? undefined : [errMsg];
  };

export const isGreaterEqual: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || Number(value) >= comparedValue
      ? undefined
      : [errorMessage];

export const isGreater: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || Number(value) > comparedValue
      ? undefined
      : [errorMessage];

export const notIsGreater: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || Number(value) < comparedValue
      ? undefined
      : [errorMessage];

export const notIsGreaterEqual: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || Number(value) <= comparedValue
      ? undefined
      : [errorMessage];

export const isArrayLengthGreater: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) => {
    if (!value) return undefined;

    const array = JSON.parse(value.toString());

    return array.length > comparedValue ? undefined : [errorMessage];
  };

export const regex: ValidatorFn =
  (errMsg: string, strPattern: string, strModifiers: string) => (v) => {
    const rgx = new RegExp(strPattern, strModifiers);

    return typeof v === 'string' && rgx.test(v) ? undefined : [errMsg];
  };

export const format: ValidatorFn =
  (errMsg: string, strPattern: string, strModifiers: string) => (v) => {
    const rgx = new RegExp(strPattern, strModifiers);

    return typeof v === 'string' && rgx.test(v) ? undefined : [errMsg];
  };

export const nullableRegex: ValidatorFn =
  (errMsg: string, strPattern: string, strModifiers: string) => (v) => {
    const rgx = new RegExp(strPattern, strModifiers);
    if (!v) return undefined;
    return typeof v === 'string' && rgx.test(v) ? undefined : [errMsg];
  };

export const isValidEmail: ValidatorFn = (errMsg: string) => (v) => {
  return v && (typeof v !== 'string' || !isEmail(v)) ? [errMsg] : undefined;
};

const REGEX_PHONE_NUMBER = '(\\+[0-9]{2}|00[0-9]{2}|0)[0-9]{9}';
export const isPhoneNumber: ValidatorFn = (errMsg: string) => (v) => {
  return new RegExp(`^${REGEX_PHONE_NUMBER}$`).test(v + '')
    ? undefined
    : [errMsg];
};

const REGEX_PHONE_NUMBER_NORMALIZED = '^0[0-9]{9}';

export const isPhoneNumberNormalized: ValidatorFn = (errMsg: string) => (v) => {
  return new RegExp(`^${REGEX_PHONE_NUMBER_NORMALIZED}$`).test(v + '')
    ? undefined
    : [errMsg];
};

export const isValidIBAN: ValidatorFn = (errMsg: string) => (v) =>
  v && !isIBAN(toString(v)) ? [errMsg] : undefined;

export const minLength: ValidatorFn = (errMsg: string, len: number) => (v) => {
  const value = toString(v);
  return value && value.length < len ? [errMsg] : undefined;
};

export const maxLength: ValidatorFn = (errMsg: string, len: number) => (v) => {
  const value = toString(v);
  return value && value.length > len ? [errMsg] : undefined;
};

export const isOlderThan: ValidatorFn =
  (
    {
      invalidDate = 'invalid date',
      tooYoung = '',
      tooOld = 'Je kunt een aanvraag doen tot 100 jaar',
    },
    minYears: number,
    maxYears: number
  ) =>
  (v) => {
    let maximumYears = 120;
    if (maxYears !== undefined) {
      maximumYears = maxYears;
    }
    if (toString(v).length === 0) return;
    const split = toString(v).split('-');
    if (split.length < 3) return [invalidDate];
    const formattedDate = split[2] + '-' + split[1] + '-' + split[0];
    const xYearsAgoDate = new Date(Date.now());
    const olderDate = new Date(Date.now());
    olderDate.setFullYear(olderDate.getFullYear() - maximumYears);
    xYearsAgoDate.setFullYear(xYearsAgoDate.getFullYear() - minYears);
    if (!isISO8601(formattedDate)) return [invalidDate];
    if (!isBefore(formattedDate, xYearsAgoDate.toISOString()))
      return [tooYoung];
    if (tooYoung.length > 0 && isBefore(formattedDate, olderDate.toISOString()))
      return [tooOld];
    return undefined;
  };

type DateDistanceArgs = [
  (
    | {
        invalidDate?: string;
        tooFarInThePast: string;
        tooFarInTheFuture?: string;
        dateToCompare?: number; // timestamp
      }
    | {
        invalidDate?: string;
        tooFarInThePast?: string;
        tooFarInTheFuture: string;
        dateToCompare?: number; // timestamp
      }
  ),
  number,
];

export const dateDistance: ValidatorFn<DateDistanceArgs> =
  (
    {
      tooFarInThePast,
      tooFarInTheFuture,
      invalidDate = 'invalid date',
      dateToCompare = undefined,
    },
    days: number
  ) =>
  (v) => {
    if (toString(v).length === 0) return;
    const split = toString(v).split('-');
    if (split.length < 3) return [invalidDate];
    const formattedDateFromUserInput =
      split[2] + '-' + split[1] + '-' + split[0];
    if (!isISO8601(formattedDateFromUserInput)) return [invalidDate];
    const dateFromUserInput = new Date(formattedDateFromUserInput);
    const now = new Date(dateToCompare ?? Date.now());
    now.setHours(0, 0, 0, 0);
    const differenceInDays = Math.round(
      Math.abs(
        (dateFromUserInput.valueOf() - now.valueOf()) / (1000 * 60 * 60 * 24)
      )
    );
    const isDateInThePast = now.valueOf() - dateFromUserInput.valueOf() > 0;
    if (
      isDateInThePast &&
      differenceInDays > days &&
      tooFarInThePast !== undefined
    )
      return [tooFarInThePast];
    if (
      !isDateInThePast &&
      differenceInDays > days &&
      tooFarInTheFuture !== undefined
    )
      return [tooFarInTheFuture];
    return undefined;
  };

const getDiffDays = (dateString: string) => {
  const date = dateString.split('-');
  const [day, month, year] = date;
  const now = new Date();

  if (year?.length < 4) {
    return -1;
  }

  const dateObj = new Date(`${year}/${month}/${day}`);
  const diffTime = dateObj.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));

  return diffDays;
};

export const daysFromNow: ValidatorFn =
  (errorMessage: string, days: number) => (value) => {
    if (!value) {
      return undefined;
    }

    const diffDays = getDiffDays(value as string);
    if (days && diffDays > days) {
      return [errorMessage];
    }

    return undefined;
  };

export const minDays: ValidatorFn =
  (errorMessage: string, days: number) => (value) => {
    if (!value) {
      return [errorMessage];
    }

    const diffDays = getDiffDays(value as string);

    if (days && diffDays < days) {
      return [errorMessage];
    }

    return undefined;
  };

export const isValidDate: ValidatorFn = (errMsg: string) => (v) => {
  const split = toString(v).split('-');
  if (split.length < 3) return [errMsg];
  const formattedDate = split[2] + '-' + split[1] + '-' + split[0];
  return isISO8601(formattedDate) ? undefined : [errMsg];
};

export const isFutureDate: ValidatorFn = (errMsg: string) => (v) => {
  try {
    const splitValue = (v + '').split('-');
    const valueAsDate = new Date(
      parseInt(splitValue[2]),
      parseInt(splitValue[1]) - 1,
      parseInt(splitValue[0])
    );
    if (valueAsDate.toString() === 'Invalid Date') return [errMsg];
    const now = new Date(Date.now());
    now.setMilliseconds(0);
    now.setSeconds(0);
    now.setMinutes(0);
    now.setHours(0);
    return valueAsDate < now ? [errMsg] : undefined;
  } catch (error) {
    return [errMsg];
  }
};

export const isValidPostalCode: ValidatorFn =
  (errMsg: string, locale?: PostalCodeLocale) => (v) =>
    isPostalCode(toString(v), locale ?? 'NL') ? undefined : [errMsg];

const toString = (v: string | number | undefined) =>
  typeof v === 'string' ? v : v + '';

export const isNumber: ValidatorFn = (errMsg: string) => (v) =>
  !isNaN(parseFloat(v + '')) ? undefined : [errMsg];

export const isString: ValidatorFn = (errMsg: string) => (v) =>
  typeof v === 'string' ? undefined : [errMsg];

export const zorgwebValidator: ValidatorFn = (errMsg: string) => () => {
  return [errMsg];
};

export const postalCodeCheck: ValidatorFn = (errMsg: string) => () => {
  return [errMsg];
};

export const mandatory: ValidatorFn = (errMsg: string) => (v) => {
  return v === 'true' || v === 'Ja' ? undefined : [errMsg];
};

export const validCbEmail: ValidatorFn = () => () => {
  return undefined;
};

export const always: ValidatorFn = () => () => undefined;

export const isFalsy: ValidatorFn = (errMsg: string) => (v) =>
  v ? undefined : [errMsg];

// To check specific cases like checkbox input value changes: "0" and "1"
const isValidNumberFromString = (val) => !Number.isNaN(+val);

export const isTruthy: ValidatorFn = (errMsg: string) => (v) => {
  if (isValidNumberFromString(v)) {
    return +(v as string) ? undefined : [errMsg];
  }
  return v ? undefined : [errMsg];
};

export const endsWith: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || value.toString().endsWith(comparedValue)
      ? undefined
      : [errorMessage];

export const notEndsWith: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || !value.toString().endsWith(comparedValue)
      ? undefined
      : [errorMessage];

export const beginsWith: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || value.toString().startsWith(comparedValue)
      ? undefined
      : [errorMessage];

export const notBeginsWith: ValidatorFn =
  (comparedValue, errorMessage: string) => (value) =>
    value === undefined || !value.toString().startsWith(comparedValue)
      ? undefined
      : [errorMessage];

export const houseNumberIsValid: ValidatorFn =
  (errMsg: string) => (_, fields) => {
    const houseNumberField = fields?.find((e) => e.name === 'housenumber');
    return !houseNumberField?.meta || houseNumberField?.meta?.isValid
      ? undefined
      : [errMsg];
  };

export const startsWith: ValidatorFn =
  (prefix: string | number, negate: boolean = false, errorMessage: string) =>
  (value) => {
    const startsWithResult =
      value === undefined || value.toString().startsWith(prefix.toString());

    const result = negate ? !startsWithResult : startsWithResult;

    return result ? undefined : [errorMessage];
  };

export const requiredIfLicensePlateHasValue: ValidatorFn =
  (errMsg: string) => (value, fields) => {
    const licensePlateField = fields?.find((f) => f.name === 'licensePlate');
    const licensePlateValue = licensePlateField?.value;
    
    // If license plate has a value but fullCarInfo is empty, show loading message
    if (licensePlateValue && (!value || value === '')) {
      return [errMsg];
    }
    
    return undefined;
  };
